import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Form, Button, Spinner, Alert, Card, Badge, Stack } from 'react-bootstrap';
import Message from '../../../Components/Message';
import apiClient from '../../../AppClient';
import PaymentModal from './PaymentModal';
import './suppliersPayments.css'; // Import your CSS file for custom styles
import { CURRENCY } from '../../../constants/Constants';

const SuppliersPaymentsDashboard = () => {
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [filteredInstallments, setFilteredInstallments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [installments, setInstallments] = useState([]);
  const [viewMode, setViewMode] = useState('payments'); 
  const [suppliers, setSuppliers] = useState([]);
  const [isLoadingInstallments, setIsLoadingInstallments] = useState(false);
  const [modalInitialValues, setModalInitialValues] = useState({
    supplier: '',
    installment: '',
    payment_method: 'كاش',
    amount: '',
    notes: ''
  });
  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (viewMode === 'payments') {
      filterTransactions();
    } else {
      filterInstallments();
    }
  }, [searchTerm, transactions, installments, viewMode]);

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        loadTransactions(),
        fetchSuppliers(),
        loadInstallments()
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('حدث خطأ أثناء تحميل البيانات الأولية');
    } finally {
      setIsLoading(false);
    }
  };


  const fetchSuppliers = async () => {
    try {
      const response = await apiClient.get('/suppliers/');
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      // Fallback to mock data if needed
      setSuppliers([
        { id: 1, name: 'أحمد خالد' },
        { id: 2, name: 'محمد علي' },
        { id: 3, name: 'سارة أحمد' }
      ]);
    }
  };

  const loadTransactions = async () => {
    try {
      const response = await apiClient.get('/transactions/supplierpayments/');
      const data = response.data;
      setTransactions(Array.isArray(data) ? data : [data]);
    } catch (error) {
      console.error('خطأ في تحميل المعاملات:', error);
      // Mock data as fallback
      setTransactions([
        {
          id: 3,
          supplier: {
            id: 3,
            supplier_type: "2س2",
            name: "romani",
            phone: "010999fgd199",
            remaining_amount: "4909.99"
          },
          transaction_date: "2025-02-28",
          amount: "30.00",
          remaining_balance: "4910.00",
          payment_method: "VISA",
          notes: "k"
        },
      ]);
    }
  };

  const loadInstallments = async (supplier = null) => {
    setIsLoadingInstallments(true);
    try {
      console.log('Loading installments for supplier:', supplier);
      const response = await apiClient.get(`/transactions/installments/?supplier=${supplier || ''}`);

      // Handle the correct response format based on the JSON structure you provided
      if (response.data && Array.isArray(response.data.installments)) {
        setInstallments(response.data.installments);
      } else if (Array.isArray(response.data)) {
        // Fallback for direct array response
        setInstallments(response.data);
      } else {
        console.warn('Unexpected response format:', response.data);
        setInstallments([]);
      }

      setError(null);
      return true;
    } catch (error) {
      console.error('خطأ في تحميل الأقساط:', error);

      if (error.response) {
        // Handle specific HTTP errors
        if (error.response.status === 404) {
          setError('الرابط غير موجود. يرجى التحقق من صحة المسار.');
        } else {
          setError(`خطأ في تحميل الأقساط (${error.response.status}). يرجى المحاولة مرة أخرى.`);
        }
      } else if (error.request) {
        // No response received
        setError('لم يتم تلقي استجابة من الخادم. يرجى التحقق من اتصالك بالإنترنت.');
      } else {
        // Setup or request error
        setError('خطأ في تحميل الأقساط. يرجى المحاولة مرة أخرى.');
      }

      setInstallments([]);
      return false;
    } finally {
      setIsLoadingInstallments(false);
    }
  };

  const filterTransactions = () => {
    if (!searchTerm.trim()) {
      setFilteredTransactions(transactions);
      return;
    }

    const filtered = transactions.filter(transaction => {
      const supplierName = transaction.supplier?.name?.toLowerCase() || '';
      const paymentMethod = transaction.payment_method?.toLowerCase() || '';
      const searchLower = searchTerm.toLowerCase();

      return (
        supplierName.includes(searchLower) ||
        paymentMethod.includes(searchLower)
      );
    });

    setFilteredTransactions(filtered);
  };

  const filterInstallments = () => {
    if (!searchTerm.trim()) {
      setFilteredInstallments(installments);
      return;
    }

    const filtered = installments.filter(installment => {
      const supplierName = installment.supplier?.toLowerCase() || '';
      const notes = installment.notes?.toLowerCase() || '';
      const searchLower = searchTerm.toLowerCase();

      return (
        supplierName.includes(searchLower) ||
        notes.includes(searchLower)
      );
    });

    setFilteredInstallments(filtered);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const addPayment = async (paymentData) => {
    try {
      // If installment_id exists, we need to mark the installment as paid
      if (paymentData.installment_id) {
        // First make the payment
        const paymentResponse = await apiClient.post('/transactions/supplierpayments/add/', {
          supplier: parseInt(paymentData.supplier, 10),
          installment: parseInt(paymentData.installment_id, 10),
          payment_method: paymentData.payment_method,
          amount: parseFloat(paymentData.amount),
          notes: paymentData.notes
        });

      } else {
        // Normal payment without installment
        await apiClient.post('/transactions/supplierpayments/add/', {
          supplier: parseInt(paymentData.supplier, 10),
          payment_method: paymentData.payment_method,
          amount: parseFloat(paymentData.amount),
          notes: paymentData.notes
        });
      }

      alert('تمت الإضافة بنجاح!');
      await loadTransactions();
      await loadInstallments();
      setShowAddModal(false);
      setError(null);
      return true; // Return true to indicate success
    } catch (error) {
      console.error('Error adding payment:', error);
      // Now we throw the error back to the modal instead of handling it here
      throw error;
    }
  };

  const handleSubmit = async (formData) => {
    // Process form data from the modal
    try {
      await addPayment(formData);
      return true;
    } catch (error) {
      throw error; // Re-throw to be caught by the modal
    }
  };

  const refreshData = async () => {
    setError(null);
    try {
      await Promise.all([
        loadTransactions(),
        loadInstallments( selectedCustomer || null )       
      ]);
    } catch (error) {
      console.error("Error refreshing data:", error);
      setError('حدث خطأ أثناء تحديث البيانات');
    }
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount).toFixed(2);
  };

  const handlePayInstallment = (installmentId) => {
    // Find the installment details
    const installment = installments.find(inst => inst.id === installmentId);
    if (!installment) return;
  
    // Find the supplier ID from the name
    const supplier = suppliers.find(c => c.name === installment.supplier);
    if (!supplier) return;
  
    // Pre-fill the payment form with installment details and open the modal
    const prefilledData = {
      supplier: supplier.id.toString(),
      installment: installmentId.toString(),
      payment_method: 'كاش', // default payment method
      amount: installment.amount.toString(),
      notes: `دفع القسط رقم ${installment.installment_number}`
    };
    
    // Set modal initial values and open it
    setShowAddModal(true);
    
    // Pass the prefilled data to the modal via initialValues
    // We need to modify the PaymentModal component to accept and use these values
    setModalInitialValues(prefilledData);
  };

  const handleCustomerChange = async (e) => {
    const supplierId = e.target.value;
    setSelectedCustomer(supplierId || null);
    

    if (supplierId) {
      // Clear previous data while loading
      setInstallments([]);
      await loadInstallments(supplierId );
    } else {
      // If no supplier selected, load all installments
      await loadInstallments();
    }
  };

  // Loading state - full page spinner
  if (isLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center min-vh-100" dir="rtl" >
        <Spinner animation="border" variant="success">
          <span className="visually-hidden">جاري التحميل...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3 py-md-4" dir="rtl">
      <Card className="shadow border-0 mb-4">
        <Card.Header className="text-white p-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3" style={{ backgroundColor: "#d1e7dd " }}>
          <h2 className="h4 h3-md mb-0" style={{ color: "#0f5132" }}>لوحة مدفوعات العملاء</h2>
          <div className="btn-group">
            <Button
              variant={viewMode === 'payments' ? "success" : "outline-success"}
              onClick={() => setViewMode('payments')}
              className="px-2 px-md-3"
              size="sm"
            >
              المدفوعات
            </Button>
            <Button
              variant={viewMode === 'installments' ? "success" : "outline-success"}
              onClick={() => setViewMode('installments')}
              className="px-2 px-md-3"
              size="sm"
            >
              الأقساط
            </Button>
          </div>
        </Card.Header>

        <Card.Body className="p-2 p-md-4">
          {/* Only show dashboard errors here, not form submission errors */}
          {error && <Alert variant="danger">{error}</Alert>}

          <Row className="mb-3 mb-md-4 align-items-center gy-2">
            <Col xs={12} md={4} className="mb-2 mb-md-0">
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder={viewMode === 'payments' ? "بحث في المعاملات..." : "بحث في الأقساط..."}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-100"
                />
              </Form.Group>
            </Col>
            {viewMode === 'installments' && (
              <>
                <Col xs={12} md={3} className="mb-2 mb-md-0">
                  <Form.Select
                    value={selectedCustomer || ''}
                    onChange={handleCustomerChange}
                    disabled={isLoadingInstallments}
                    className="w-100"
                  >
                    <option value="">اختر عميل</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={12} md={3} className="mb-2 mb-md-0">
                </Col>
              </>
            )}
            <Col xs={12} md={viewMode === 'installments' ? 2 : 8} className="d-flex justify-content-start justify-content-md-end">
              <Stack direction="horizontal" gap={2} className="flex-wrap">
                {isLoadingInstallments && viewMode === 'installments' && (
                  <Spinner animation="border" size="sm" className="me-2" />
                )}
                <Button
                  variant="outline-secondary"
                  onClick={refreshData}
                  disabled={isLoadingInstallments}
                  className="px-2 px-md-3"
                  size="sm"
                >
                  🔄 تحديث
                </Button>
                <Button
                  variant="success"
                  onClick={() => setShowAddModal(true)}
                  className="px-2 px-md-3"
                  size="sm"
                >
                  <i className="fas fa-plus"></i> إضافة
                </Button>
              </Stack>
            </Col>
          </Row>

          {isLoadingInstallments && viewMode === 'installments' ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="success" />
              <p className="mt-3">جاري تحميل الأقساط...</p>
            </div>
          ) : filteredTransactions.length === 0 && viewMode === 'payments' ? (
            <div className="text-center py-4">
              <div className="empty-state">
                <span className="display-6">📄</span>
                <p className="mt-2">لم يتم العثور على معاملات</p>
              </div>
            </div>
          ) : filteredInstallments.length === 0 && viewMode === 'installments' ? (
            <div className="text-center py-4">
              <div className="empty-state">
                <span className="display-6">📄</span>
                <p className="mt-2">لم يتم العثور على أقساط</p>
              </div>
            </div>
          ) : viewMode === 'payments' ? (
            <div className="table-responsive">
              <Table bordered hover responsive className="table-sm table-md">
                <thead className="bg-light">
                  <tr>
                    <th>العميل</th>
                    <th>المبلغ</th>
                    <th>الباقي</th>
                    <th>طريقة الدفع</th>
                    <th className="d-none d-md-table-cell">تاريخ الدفع</th>
                    <th className="d-none d-md-table-cell">ملاحظات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map(transaction => (
                    <tr key={transaction.id}>
                      <td>{transaction.supplier?.name || 'غير معروف'}</td>
                      <td>{formatCurrency(transaction.amount)} {CURRENCY}</td>
                      <td>{formatCurrency(transaction.remaining_balance)} {CURRENCY}</td>
                      <td>{transaction.payment_method}</td>
                      <td className="d-none d-md-table-cell">{transaction.transaction_date}</td>
                      <td className="d-none d-md-table-cell">
                        <div className="text-truncate" style={{maxWidth: "150px"}}>
                          {transaction.notes}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              
              {/* Mobile list view for payment transactions (visible only on small screens) */}
              <div className="d-md-none">
                {filteredTransactions.map(transaction => (
                  <Card key={transaction.id} className="mb-2 mobile-transaction-card">
                    <Card.Body className="p-2">
                      <div className="d-flex justify-content-between mb-1">
                        <strong>{transaction.supplier?.name || 'غير معروف'}</strong>
                        <Badge bg="primary">{transaction.payment_method}</Badge>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span>المبلغ:</span>
                        <span>{formatCurrency(transaction.amount)} {CURRENCY}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span>الباقي:</span>
                        <span>{formatCurrency(transaction.remaining_balance)} {CURRENCY}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span>التاريخ:</span>
                        <span>{transaction.transaction_date}</span>
                      </div>
                      {transaction.notes && (
                        <small className="text-muted d-block text-truncate">{transaction.notes}</small>
                      )}
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <Table bordered hover responsive className="table-sm table-md">
                <thead className="bg-light">
                  <tr>
                    <th>العميل</th>
                    <th>قسط</th>
                    <th>المبلغ</th>
                    <th className="d-none d-md-table-cell">تاريخ الإنشاء</th>
                    <th>تاريخ الاستحقاق</th>
                    <th>الحالة</th>
                    <th className="d-none d-md-table-cell">ملاحظات</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInstallments.map(installment => (
                    <tr key={installment.id} className="">
                      <td>{installment?.supplier || 'غير معروف'}</td>
                      <td>{installment.installment_number}</td>
                      <td>{formatCurrency(installment.amount)} {CURRENCY}</td>
                      <td className="d-none d-md-table-cell">{new Date(installment.created_at).toLocaleDateString('ar-EG')}</td>
                      <td>{new Date(installment.due_date).toLocaleDateString('ar-EG')}</td>
                      <td>
                        {installment.is_paid ? (
                          <Badge bg="success">مدفوع</Badge>
                        ) : new Date(installment.due_date) < new Date() ? (
                          <Badge bg="danger">متأخر</Badge>
                        ) : (
                          <Badge bg="warning">مستحق</Badge>
                        )}
                      </td>
                      <td className="d-none d-md-table-cell">
                        <div className="text-truncate" style={{maxWidth: "150px"}}>
                          {installment.notes}
                        </div>
                      </td>
                      <td>
                        {!installment.is_paid && (
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => handlePayInstallment(installment.id)}
                          >
                            سداد
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              
              {/* Mobile list view for installments (visible only on small screens) */}
              <div className="d-md-none">
                {filteredInstallments.map(installment => (
                  <Card key={installment.id} className="mb-2 mobile-installment-card">
                    <Card.Body className="p-2">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <strong>{installment?.supplier || 'غير معروف'}</strong>
                        <Badge bg={
                          installment.is_paid ? "success" : 
                          new Date(installment.due_date) < new Date() ? "danger" : 
                          "warning"
                        }>
                          {installment.is_paid ? "مدفوع" : 
                           new Date(installment.due_date) < new Date() ? "متأخر" : 
                           "مستحق"}
                        </Badge>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span>قسط رقم:</span>
                        <span>{installment.installment_number}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span>المبلغ:</span>
                        <span>{formatCurrency(installment.amount)} {CURRENCY}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span>تاريخ الاستحقاق:</span>
                        <span>{new Date(installment.due_date).toLocaleDateString('ar-EG')}</span>
                      </div>
                      {installment.notes && (
                        <small className="text-muted d-block text-truncate">{installment.notes}</small>
                      )}
                      {!installment.is_paid && (
                        <div className="text-end mt-2">
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => handlePayInstallment(installment.id)}
                          >
                            سداد
                          </Button>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      <PaymentModal
  show={showAddModal}
  handleClose={() => {
    setShowAddModal(false);
    // Reset modal initial values when closing
    setModalInitialValues({
      supplier: '',
      installment: '',
      payment_method: 'كاش',
      amount: '',
      notes: ''
    });
  }}
  suppliers={suppliers}
  handleSubmit={handleSubmit}
  apiClient={apiClient}  // Pass apiClient as a prop
  mode="add"
  initialValues={modalInitialValues}
/>
    </Container>
  );
};

export default SuppliersPaymentsDashboard;